import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import type { MegaMenuItem } from "./MegaMenu";

interface MegaMenuDropdownProps {
  item: MegaMenuItem;
  onClose: () => void;
}

export const MegaMenuDropdown = ({ item, onClose }: MegaMenuDropdownProps) => {
  const columns = item.columns || [];
  const images = item.images || [];
  const columnCount = columns.length;

  // Adaptive grid: fewer columns → more space for images
  const gridColsClass =
    columnCount >= 4
      ? "grid-cols-4"
      : columnCount === 3
      ? "grid-cols-3"
      : columnCount === 2
      ? "grid-cols-2"
      : "grid-cols-1";

  // How many images to show based on available space
  const maxImages = columnCount <= 1 ? 3 : columnCount <= 2 ? 2 : 1;
  const visibleImages = images.slice(0, maxImages);

  return (
    <div className="max-w-[1400px] mx-auto px-8 py-8">
      <div className="flex gap-10">
        {/* Text columns */}
        <div className={`flex-1 grid ${gridColsClass} gap-8`}>
          {columns.map((col, colIdx) => (
            <motion.div
              key={col.heading}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: colIdx * 0.05, duration: 0.25 }}
            >
              <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-3 pb-2 border-b border-border">
                {col.heading}
              </p>
              <div className="flex flex-col gap-1">
                {col.items.map((sub) => (
                  <Link
                    key={sub.label}
                    to={sub.href}
                    className="text-[13px] text-muted-foreground hover:text-foreground transition-colors py-1 group flex items-center gap-1.5"
                    onClick={onClose}
                  >
                    <span className="group-hover:translate-x-0.5 transition-transform duration-200">
                      {sub.label}
                    </span>
                  </Link>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Featured images — adaptive count */}
        {visibleImages.length > 0 && (
          <div className="hidden lg:flex gap-4 flex-shrink-0">
            {visibleImages.map((img, imgIdx) => (
              <Link
                key={img.href}
                to={img.href}
                className="w-48 group"
                onClick={onClose}
              >
                <motion.div
                  initial={{ opacity: 0, scale: 0.97 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.1 + imgIdx * 0.07, duration: 0.3 }}
                  className="aspect-[3/4] overflow-hidden bg-muted rounded-sm"
                >
                  <img
                    src={img.src}
                    alt={img.alt}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
                  />
                </motion.div>
                <p className="text-[12px] font-medium text-foreground mt-2 group-hover:text-primary transition-colors">
                  {img.caption} →
                </p>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
